import React, { useState, useEffect } from 'react';
import { useCart } from './CartContext';
import { fetchProducts } from '../api/woocommerce';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { CSSTransition } from 'react-transition-group';
import { useRef } from 'react';

const SearchSidebar = () => {
  const { isSearchOpen, closeSearch } = useCart();
  const { formatPrice } = useCurrency();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSearchSidebar, setShowSearchSidebar] = useState(false);
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const nodeRef = useRef(null);
  const overlayRef = useRef(null);

  // Animation styles
  const animationStyles = `
    <style>
      @keyframes slideInFromRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      @keyframes slideOutToRight {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }

      .animate-slideInFromRight {
        animation: slideInFromRight 0.1s ease-in-out forwards;
      }

      .animate-slideOutToRight {
        animation: slideOutToRight 0.1s ease-in-out forwards;
      }
    </style>
  `;

  // Handle search open/close with animation
  useEffect(() => {
    if (isSearchOpen) {
      // Opening search
      setTimeout(() => {
        setShowSearchSidebar(true);
      }, 50);
    } else {
      // Closing search
      setShowSearchSidebar(false);
    }
  }, [isSearchOpen]);

  // Handle search when searchTerm changes
  useEffect(() => {
    const performSearch = async () => {
      if (searchTerm.trim().length === 0) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      try {
        const results = await fetchProducts({
          searchTerm: searchTerm.trim(),
          perPage: 12,
          language: currentLanguage
        });
        setSearchResults(results.products);
      } catch (error) {
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, currentLanguage]);



  // Handle overlay click
  const handleOverlayClick = () => {
    handleClose();
  };

  // Close search when escape key is pressed

  // Handle open/close with animation timing
  useEffect(() => {
    if (isSearchOpen) {
      // Small delay to allow DOM to register the element before animating
      setTimeout(() => setShowSearchSidebar(true), 10);
    } else {
      setShowSearchSidebar(false);
    }
  }, [isSearchOpen]);

  // Handle close with animation
  const handleClose = () => {
    setShowSearchSidebar(false);
    setTimeout(() => {
      closeSearch();
      setSearchTerm('');
    }, 300); // Match animation duration
  };

  if (!isSearchOpen) return null;

  return (
    <>
      {/* Overlay */}
      <CSSTransition
        in={showSearchSidebar}
        timeout={300}
        classNames="search-overlay"
        unmountOnExit
        nodeRef={overlayRef}
      >
        <div
          ref={overlayRef}
          className="fixed inset-0 bg-black bg-opacity-80 z-50"
          onClick={handleClose}
        />
      </CSSTransition>

      {/* Sidebar */}
      <CSSTransition
        in={showSearchSidebar}
        timeout={300}
        classNames="search-sidebar"
        unmountOnExit
        nodeRef={nodeRef}
      >
        <div
          ref={nodeRef}
          className="fixed inset-y-0 right-0 w-full max-w-[358px] bg-white z-50 shadow-xl"
        >
          {/* Your existing search content */}
          <div className="h-full flex flex-col px-2">
            {/* Search Input */}
            <div className="pt-[10px]">
              <div className="relative">
                {/* Search Icon */}
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="black" strokeMiterlimit="10" />
                    <path d="M14 14L11.1 11.1" stroke="black" strokeMiterlimit="10" />
                  </svg>
                </div>

                <input
                  type="text"
                  placeholder={t('search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-[36px] text-14 font-normal p-3 pl-10 pr-10 border border-black focus:outline-none text-14 placeholder-[#BDBDBD] placeholder:text-14 placeholder:font-normal"
                  autoFocus
                />

                <button
                  onClick={handleClose}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 focus:outline-none"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_8240_166)">
                      <path d="M14.1745 14.1745L1.82544 1.82545" stroke="black" strokeMiterlimit="10" />
                      <path d="M1.82544 14.1745L14.1745 1.82545" stroke="black" strokeMiterlimit="10" />
                    </g>
                    <defs>
                      <clipPath id="clip0_8240_166">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </button>
              </div>
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-y-auto pt-[55px]">
              {loading && (
                <div className="text-center py-4">
                  <p className="text-14 font-light">{t('search.searching')}</p>
                </div>
              )}

              {!loading && searchTerm && searchResults.length === 0 && (
                <div className="py-4">
                  <p className="text-14 font-light">{t('search.noResults', { searchTerm: searchTerm })}</p>
                </div>
              )}

              {!loading && searchResults.length > 0 && (
                <div>
                  {/* 3-Column Grid Layout for modal */}
                  <div className="grid grid-cols-3 gap-x-[9px] gap-y-[16px]">
                    {searchResults.map((product) => (
                      <div key={product.id}>
                        <a
                          href={`/store/product/${product.slug}`}
                          className="block"
                          onClick={handleClose}
                        >
                          {/* Product Image */}
                          <div className="w-full aspect-square bg-[#BDBDBD]">
                            {product.images && product.images.length > 0 ? (
                              <img
                                src={product.images[0].src}
                                alt={product.name}
                                className="w-full h-full object-cover border border-black"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-[#F5F5F5] border border-black">
                                <span className="text-black text-10">{t('productGrid.noImage')}</span>
                              </div>
                            )}
                          </div>

                          {/* Product Info */}
                          <div className="pt-2">
                            <h3 className="text-14 font-light mb-[4px] line-clamp-2">{product.name}</h3>
                            <p className="text-14 font-normal">{formatPrice(product.price)}</p>
                          </div>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* View All Button */}
            {!loading && searchTerm && searchResults.length !== 0  && (
              <div className="border-t border-black pt-2 pb-[10px]">
                <button
                  onClick={() =>{
                    handleClose();
                      window.location.href = `/products?search=${encodeURIComponent(searchTerm.trim())}`;
                  }}
                  className="w-full bg-black text-white pt-[10px] pb-[9px] text-14 font-normal"
                >
                  {t('search.viewAll')}
                </button>
              </div>
            )}
          </div>
        </div>
      </CSSTransition>
    </>
  );
}

export default SearchSidebar